import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Copy, RefreshCw, Ban, ExternalLink, Send, Search, Inbox } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { dealInvitesApi, type DealInvite } from '../../services/dealsApi';
import { RubyLoader } from '../RubyLoader';
import { RubyGemMark } from '../RubyGemMark';

/**
 * INVITES — every deal sent from Command Center, and how far each prospect got.
 *
 * Approved mockup: `applications/platform-app/mockups/deal-invite-offer-page.html` (screen ③).
 *
 * The stage is the SERVER's (`invite.stage`): sent → opened → signed_up, or
 * expired / revoked. Nothing is inferred here from timestamps, so this list and
 * the offer page can never disagree about whether a link still works.
 */

type Stage = DealInvite['stage'];

const STAGE: Record<Stage, { label: string; cls: string; step: number }> = {
  sent: { label: 'Sent', cls: 'bg-stone-100 text-stone-600', step: 1 },
  opened: { label: 'Opened', cls: 'bg-blue-50 text-blue-700', step: 2 },
  signed_up: { label: 'Signed up', cls: 'bg-emerald-50 text-emerald-700', step: 3 },
  expired: { label: 'Expired', cls: 'bg-red-50 text-red-700', step: 0 },
  revoked: { label: 'Revoked', cls: 'bg-stone-200 text-stone-500', step: 0 },
};

const FILTERS: { id: 'all' | Stage; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'sent', label: 'Sent' },
  { id: 'opened', label: 'Opened' },
  { id: 'signed_up', label: 'Signed up' },
  { id: 'expired', label: 'Expired' },
];

const ago = (ms?: number) => {
  if (!ms) return '';
  const s = Math.max(0, (Date.now() - ms) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};
const day = (ms?: number) =>
  ms ? new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

interface Props {
  reloadKey: number;
  onSendDeal: () => void;
  onToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const InvitesView: React.FC<Props> = ({ reloadKey, onSendDeal, onToast }) => {
  const navigate = useNavigate();
  const [invites, setInvites] = useState<DealInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | Stage>('all');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dealInvitesApi.list();
      setInvites(res.invites || []);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Invites could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, reloadKey]);

  const counts = useMemo(() => {
    const c = { sent: invites.length, opened: 0, signed_up: 0, expired: 0 };
    for (const i of invites) {
      if (i.stage === 'opened' || i.stage === 'signed_up' || (i.openCount ?? 0) > 0) c.opened++;
      if (i.stage === 'signed_up') c.signed_up++;
      if (i.stage === 'expired') c.expired++;
    }
    return c;
  }, [invites]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return invites.filter((i) =>
      (filter === 'all' || i.stage === filter) &&
      (!q || [i.businessName, i.contactName, i.email, i.dealCode, i.dealName].some((v) => v?.toLowerCase().includes(q))),
    );
  }, [invites, filter, search]);

  const act = async (invite: DealInvite, action: 'resend' | 'revoke' | 'copy') => {
    if (action === 'copy') {
      await navigator.clipboard.writeText(invite.url).catch(() => undefined);
      onToast('Offer link copied', 'success');
      return;
    }
    setBusy(`${invite.inviteId}:${action}`);
    try {
      if (action === 'resend') {
        await dealInvitesApi.resend(invite.inviteId);
        onToast(`Offer re-sent to ${invite.email}`, 'success');
      } else {
        await dealInvitesApi.revoke(invite.inviteId);
        onToast('Invite revoked — the link no longer opens the offer', 'info');
      }
      await load();
    } catch (e: any) {
      onToast(e?.message || `Could not ${action} the invite`, 'error');
    } finally {
      setBusy(null);
    }
  };

  const tiles = [
    { label: 'Sent', value: counts.sent, pct: 100, bar: 'bg-[#DC143C]' },
    { label: 'Opened', value: counts.opened, pct: counts.sent ? (counts.opened / counts.sent) * 100 : 0, bar: 'bg-[#DC143C]' },
    { label: 'Signed up', value: counts.signed_up, pct: counts.sent ? (counts.signed_up / counts.sent) * 100 : 0, bar: 'bg-emerald-600', tone: 'text-emerald-700' },
    { label: 'Expired', value: counts.expired, pct: counts.sent ? (counts.expired / counts.sent) * 100 : 0, bar: 'bg-red-600', tone: 'text-red-700' },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {tiles.map((t) => (
          <div key={t.label} className="rounded-2xl border border-stone-200 bg-white px-4 py-3.5">
            <p className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-stone-400">{t.label}</p>
            <p className={`mt-1 text-[28px] font-semibold tracking-tight ${t.tone || 'text-stone-900'}`}>
              {loading && !invites.length ? <RubyGemMark size={24} drawing title="Loading" /> : t.value}
            </p>
            <div className="mt-2.5 h-1 overflow-hidden rounded bg-stone-100">
              <div className={`h-full ${t.bar}`} style={{ width: `${Math.round(t.pct)}%` }} />
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex min-h-[44px] w-full max-w-sm items-center gap-2 rounded-xl border border-stone-200 bg-white px-3 text-stone-400 focus-within:border-stone-900">
          <Search size={16} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search invites…"
            className="h-10 flex-1 bg-transparent text-sm text-stone-900 outline-none"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={`min-h-[44px] rounded-xl border px-4 text-sm font-medium transition ${
                filter === f.id ? 'border-stone-900 bg-stone-900 text-white' : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="relative overflow-x-auto rounded-2xl border border-stone-200 bg-white">
        {loading && invites.length === 0 ? (
          <RubyLoader variant="section" label="Loading invites…" />
        ) : error ? (
          <div className="p-6 text-sm text-red-700">
            {error}{' '}
            <button type="button" onClick={load} className="font-semibold underline">Try again</button>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
            <Inbox size={36} className="text-stone-300" />
            <p className="text-sm text-stone-500">
              {invites.length ? 'No invite matches this filter.' : 'No deals sent yet.'}
            </p>
            {!invites.length && (
              <button
                type="button"
                onClick={onSendDeal}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-[#DC143C] px-5 text-sm font-semibold text-white hover:bg-[#B91C1C]"
              >
                <Send size={16} /> Send your first deal
              </button>
            )}
          </div>
        ) : (
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b border-stone-200 bg-[#FBFBFC] text-left">
                {['Prospect', 'Deal', 'Status', 'Progress', 'Activity', ''].map((h) => (
                  <th key={h} className="px-4 py-3.5 font-mono text-[9.5px] font-medium uppercase tracking-[0.18em] text-stone-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((i) => {
                const st = STAGE[i.stage] || STAGE.sent;
                const live = i.stage === 'sent' || i.stage === 'opened';
                return (
                  <tr key={i.inviteId} className="border-b border-stone-100 last:border-0">
                    <td className="px-4 py-3.5">
                      <p className="font-semibold text-stone-900">{i.businessName}</p>
                      <p className="text-xs text-stone-400">{i.contactName} · {i.email}</p>
                    </td>
                    <td className="px-4 py-3.5">
                      <p className="text-stone-800">{i.dealName}</p>
                      <span className="rounded-md border border-[#DC143C]/40 px-1.5 font-mono text-[10.5px] text-[#DC143C]">{i.dealCode}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${st.cls}`}>{st.label}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1">
                        {[1, 2, 3].map((n) => (
                          <i
                            key={n}
                            className={`h-[5px] w-6 rounded ${
                              i.stage === 'signed_up' ? 'bg-emerald-600'
                                : n <= st.step ? 'bg-[#DC143C]'
                                : n === 2 && (i.openCount ?? 0) > 0 ? 'bg-[#DC143C]'
                                : n === 1 ? 'bg-[#DC143C]'
                                : 'bg-stone-200'
                            }`}
                          />
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-stone-700">
                      {i.stage === 'signed_up' ? (
                        <>
                          <p>Signed up {day(i.signedUpAt)}</p>
                          {i.tenantId && (
                            <button type="button" onClick={() => navigate(`/tenant/${i.tenantId}`)} className="text-xs font-medium text-[#DC143C] hover:underline">
                              Open tenant ↗
                            </button>
                          )}
                        </>
                      ) : (i.openCount ?? 0) > 0 ? (
                        <p>Viewed {i.openCount}× · last {ago(i.lastOpenedAt)}</p>
                      ) : (
                        <p className="text-stone-400">Not opened yet</p>
                      )}
                      <p className="text-xs text-stone-400">
                        {i.stage === 'expired' ? `Expired ${day(i.expiresAt)}`
                          : `Sent ${day(i.lastSentAt || i.createdAt)}${i.sentByName ? ` by ${i.sentByName}` : ''}`}
                      </p>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex justify-end gap-1.5">
                        {i.stage === 'signed_up' && i.tenantId ? (
                          <IconBtn title="Open tenant" onClick={() => navigate(`/tenant/${i.tenantId}`)}><ExternalLink size={16} /></IconBtn>
                        ) : (
                          <>
                            {live && <IconBtn title="Copy link" onClick={() => act(i, 'copy')}><Copy size={16} /></IconBtn>}
                            {i.stage !== 'revoked' && (
                              <IconBtn
                                title={i.stage === 'expired' ? 'Re-send with a fresh 14-day link' : 'Re-send the email'}
                                onClick={() => act(i, 'resend')}
                                busy={busy === `${i.inviteId}:resend`}
                              >
                                <RefreshCw size={16} />
                              </IconBtn>
                            )}
                            {live && (
                              <IconBtn title="Revoke — the link stops working" onClick={() => act(i, 'revoke')} busy={busy === `${i.inviteId}:revoke`}>
                                <Ban size={16} />
                              </IconBtn>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const IconBtn: React.FC<{ title: string; onClick: () => void; busy?: boolean; children: React.ReactNode }> = ({ title, onClick, busy, children }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    onClick={onClick}
    disabled={busy}
    className="grid h-11 w-11 place-items-center rounded-xl border border-stone-200 bg-white text-stone-500 transition hover:border-stone-300 hover:text-stone-900 disabled:opacity-50"
  >
    {busy ? <RubyGemMark size={16} drawing color="currentColor" /> : children}
  </button>
);

export default InvitesView;
